'use client'

import { useEffect, useState, type CSSProperties } from 'react'

export type SubnavLink = {
  /** id of the section the link jumps to, without the `#` */
  id: string
  label: string
  /** the link's dot colour, e.g. `var(--ink)` */
  color?: string
}

/** Where the sub nav sits once pinned: below the 72px header, plus the
 *  reference script's 26px reading line. */
const LINE = 72 + 26

/**
 * The shell's sticky sub nav (`.subnav` in shell.css) as a component, for
 * pages that are not verbatim reference ports. It does what the reference
 * pages' inline scrollspy scripts do, with observers instead of a scroll
 * listener:
 *
 * - the link of the section in the middle of the viewport is active, marked
 *   with `aria-current="location"` as well as the highlight;
 * - over an ink or accent sheet the bar takes that surface (`is-dark` /
 *   `is-accent`);
 * - once the `lastId` section has scrolled past, the bar slides away
 *   (`is-gone`) and turns `inert`, so its hidden links leave the tab order.
 */
export default function ShellSubnav({
  links,
  lastId,
  label,
  ariaLabel,
  many = false,
}: {
  links: SubnavLink[]
  /** the bar hides once this section has scrolled above it */
  lastId: string
  /** the mono note at the end of the bar */
  label?: string
  ariaLabel: string
  /** many links: the shell hides `label` on narrower screens */
  many?: boolean
}) {
  const [active, setActive] = useState<string | null>(null)
  const [surface, setSurface] = useState<'paper' | 'ink' | 'accent'>('paper')
  const [gone, setGone] = useState(false)

  // Scrollspy: the section crossing the middle band of the viewport.
  useEffect(() => {
    const targets = links
      .map(link => document.getElementById(link.id))
      .filter((el): el is HTMLElement => el !== null)
    const observer = new IntersectionObserver(
      entries =>
        entries.forEach(entry => {
          if (entry.isIntersecting) setActive(entry.target.id)
        }),
      { rootMargin: '-35% 0px -55% 0px' }
    )
    targets.forEach(target => observer.observe(target))
    return () => observer.disconnect()
  }, [links])

  // Surface and end state: what sits on the one-pixel line under the bar.
  useEffect(() => {
    const sheets = Array.from(
      document.querySelectorAll<HTMLElement>('.sheet--ink, .sheet--accent')
    )
    const last = document.getElementById(lastId)
    const onLine = new Set<Element>()
    let observer: IntersectionObserver | null = null

    const watch = () => {
      observer?.disconnect()
      onLine.clear()
      const below = Math.max(0, window.innerHeight - LINE - 1)
      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.target === last) {
              setGone(
                !entry.isIntersecting && entry.boundingClientRect.bottom < LINE
              )
              return
            }
            if (entry.isIntersecting) onLine.add(entry.target)
            else onLine.delete(entry.target)
          })
          // Sheets overlap by a radius; the first in page order wins, as in
          // the reference script.
          const sheet = sheets.find(s => onLine.has(s))
          setSurface(
            !sheet
              ? 'paper'
              : sheet.classList.contains('sheet--accent')
                ? 'accent'
                : 'ink'
          )
        },
        { rootMargin: `-${LINE}px 0px -${below}px 0px` }
      )
      sheets.forEach(sheet => observer?.observe(sheet))
      if (last) observer.observe(last)
    }

    watch()
    window.addEventListener('resize', watch)
    return () => {
      window.removeEventListener('resize', watch)
      observer?.disconnect()
    }
  }, [lastId])

  const classes = ['subnav']
  if (many) classes.push('subnav--many')
  if (surface === 'ink') classes.push('is-dark')
  if (surface === 'accent') classes.push('is-accent')
  if (gone) classes.push('is-gone')

  return (
    <nav aria-label={ariaLabel} className={classes.join(' ')} inert={gone}>
      <div className='container subnav__inner'>
        {links.map(link => (
          <a
            key={link.id}
            href={`#${link.id}`}
            className={active === link.id ? 'is-active' : undefined}
            aria-current={active === link.id ? 'location' : undefined}
            style={
              link.color ? ({ '--c': link.color } as CSSProperties) : undefined
            }
          >
            <i />
            {link.label}
          </a>
        ))}
        <span className='subnav__spacer' />
        {label && <span className='mono'>{label}</span>}
      </div>
    </nav>
  )
}
